import { useEffect, useState } from "react";
import { SCENARIOS, STEPS, scenarioById, stepAt, type Scenario } from "./scenarios";
import "./trust-exchange.css";

const CHECKS = [
  ["Signature", "Ed25519 over the canonical artifact"],
  ["Signing key", "Customer-held root, kid matches the trust domain"],
  ["Evidence refs", "Citations hash to the bytes that were actually released"],
  ["Authorization", "The Cedar decision is bound into the artifact"],
  ["Transparency log", "Inclusion proof on the append-only log"],
] as const;

export function TrustExchange() {
  const [scenarioId, setScenarioId] = useState((SCENARIOS[0] as Scenario).id);
  const scenario = scenarioById(scenarioId);
  const [cursor, setCursor] = useState(-1);
  const [running, setRunning] = useState(false);
  const [pinned, setPinned] = useState<number | null>(null);
  const [independent, setIndependent] = useState(true);

  useEffect(() => {
    setCursor(-1);
    setRunning(false);
    setPinned(null);
    setIndependent(true);
  }, [scenarioId]);

  useEffect(() => {
    if (!running) return;
    if (cursor >= scenario.settleAt) {
      setRunning(false);
      return;
    }
    const id = window.setTimeout(() => setCursor((c) => c + 1), 780);
    return () => window.clearTimeout(id);
  }, [running, cursor, scenario.settleAt, scenario.id]);

  const settled = cursor >= scenario.settleAt;
  const refused = settled && scenario.verdict === "deny";
  const permitted = settled && scenario.verdict === "permit";
  const view = pinned != null && pinned <= cursor ? pinned : cursor;
  const record = view >= 0 ? scenario.records[stepAt(view).key] : null;
  const travel = cursor < 0 ? 0 : cursor;

  function run() {
    setPinned(null);
    if (running) {
      setRunning(false);
      return;
    }
    if (cursor < 0 || cursor >= scenario.settleAt) setCursor(0);
    setRunning(true);
  }

  function step() {
    setRunning(false);
    setPinned(null);
    setCursor((c) => Math.min(scenario.settleAt, c + 1));
  }

  function reset() {
    setRunning(false);
    setPinned(null);
    setCursor(-1);
  }

  const status =
    cursor < 0
      ? "Waiting to run"
      : refused
        ? `Refused · ${scenario.code}`
        : permitted
          ? "Permitted · receipt signed"
          : running
            ? `${stepAt(cursor).label}…`
            : stepAt(cursor).label;

  return (
    <section className="bfx" aria-label="Illustrative Bifrost exchange">
      <header className="bfx-header">
        <div className="bfx-brand">
          <BridgeMark />
          <div>
            <p className="bfx-kicker">Bifrost</p>
            <p className="bfx-sub">The Autonomous Trust Center</p>
          </div>
        </div>
        <p className="bfx-aside">Neither side hands its keys to the other.</p>
      </header>

      <div className="bfx-hero">
        <h2>The trust layer for autonomous organizations.</h2>
        <p className="bfx-lede">
          Establish identity. Enforce authority. Govern every exchange. Verify the outcome.
        </p>
      </div>

      <div className="bfx-scenarios" role="radiogroup" aria-label="Exchange scenarios">
        {SCENARIOS.map((item) => {
          const on = item.id === scenario.id;
          return (
            <button
              key={item.id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => setScenarioId(item.id)}
              className={on ? "bfx-chip is-on" : "bfx-chip"}
            >
              {item.title}
            </button>
          );
        })}
      </div>

      <section className="bfx-panel">
        <p className="bfx-kicker">Request</p>
        <p className="bfx-muted">{scenario.blurb}</p>
        <p className="bfx-request">{scenario.request}</p>
        <div className="bfx-actions">
          <button type="button" onClick={run} className="bfx-btn bfx-btn-primary">
            <PlayIcon />
            {running ? "Pause" : cursor >= scenario.settleAt ? "Run again" : "Run exchange"}
          </button>
          <button type="button" onClick={step} disabled={settled} className="bfx-btn bfx-btn-line">
            Step
          </button>
          <button type="button" onClick={reset} className="bfx-btn">
            <ResetIcon />
            Reset
          </button>
          <p className="bfx-sr" aria-live="polite">
            {status}. {settled ? scenario.reason : ""}
          </p>
        </div>
      </section>

      <section className="bfx-parties">
        <Party
          tag="Evidence owner · decides"
          org={scenario.owner.org}
          lines={[
            scenario.owner.domain,
            scenario.owner.principal,
            scenario.owner.assurance,
            scenario.owner.domainId,
          ]}
          items={scenario.evidence.map((e) => `${e.name} — ${e.note}`)}
          itemLabel="Evidence boundary"
        />
        <Bridge
          travel={travel}
          refused={refused}
          permitted={permitted}
          status={status}
          idle={cursor < 0}
          line={cursor >= 0 ? stepAt(cursor).line : ""}
        />
        <Party
          tag="Requesting agent · asks"
          org={scenario.requester.org}
          lines={[
            scenario.requester.agent,
            scenario.requester.principal,
            `Purpose ${scenario.requester.purpose}`,
            `Expires ${scenario.requester.expires}`,
          ]}
          items={scenario.requester.chain}
          itemLabel="Delegation chain"
        />
      </section>

      <ol className="bfx-steps">
        {STEPS.map((stepItem, index) => {
          const skipped = settled && index > scenario.settleAt;
          const isRefusal = refused && index === scenario.settleAt;
          const done = (cursor > index || permitted) && !isRefusal && !skipped;
          const active = index === cursor && !settled;
          const reached = index <= cursor && !skipped;
          const stepClass = [
            "bfx-step",
            isRefusal ? "is-refusal" : view === index && reached ? "is-view" : "",
            skipped ? "is-skipped" : "",
          ]
            .filter(Boolean)
            .join(" ");
          const numClass = [
            "bfx-num",
            isRefusal ? "is-bad" : done ? "is-ok" : active ? "is-ice" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <li key={stepItem.key}>
              <button
                type="button"
                disabled={!reached}
                onClick={() => setPinned(index)}
                className={stepClass}
              >
                <span className={numClass}>{stepItem.n.slice(1)}</span>
                <span className="bfx-step-label">{stepItem.label}</span>
              </button>
            </li>
          );
        })}
      </ol>

      {record && view >= 0 ? (
        <section className="bfx-lower">
          <article className="bfx-panel">
            <p className="bfx-kicker">
              {stepAt(view).n} {stepAt(view).label}
            </p>
            <h2>{record.headline}</h2>
            <p className="bfx-muted">{record.detail}</p>
            {record.claims.length > 0 ? (
              <dl className="bfx-claims">
                {record.claims.map((claim) => (
                  <div key={claim.k} className="bfx-claim">
                    <dt>{claim.k}</dt>
                    <dd>{claim.v}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
            {stepAt(view).key === "authorize" ? <pre className="bfx-policy">{scenario.policy}</pre> : null}
          </article>
          <Artifact
            scenario={scenario}
            settled={settled}
            independent={independent}
            onIndependent={setIndependent}
          />
        </section>
      ) : (
        <section className="bfx-panel">
          <h2>Run the exchange.</h2>
          <p className="bfx-muted">
            Bifrost sits between the evidence owner and the requesting agent. It governs the exchange.
            It does not become either organization’s infrastructure.
          </p>
        </section>
      )}

      <footer className="bfx-foot">
        <p>OAuth 2.1 · DPoP · MCP · A2A v1.0 · OpenID AuthZEN · Cedar</p>
        <p>Illustrative exchange. Not a production signing key.</p>
      </footer>
    </section>
  );
}

function Party({
  tag,
  org,
  lines,
  items,
  itemLabel,
}: {
  tag: string;
  org: string;
  lines: string[];
  items: string[];
  itemLabel: string;
}) {
  return (
    <article className="bfx-panel bfx-party">
      <p className="bfx-kicker">{tag}</p>
      <h2>{org}</h2>
      <ul className="bfx-lines">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <div>
        <p className="bfx-item-label">{itemLabel}</p>
        <ul className="bfx-items">
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    </article>
  );
}

function Bridge({
  travel,
  refused,
  permitted,
  status,
  idle,
  line,
}: {
  travel: number;
  refused: boolean;
  permitted: boolean;
  status: string;
  idle: boolean;
  line: string;
}) {
  return (
    <div className="bfx-panel bfx-bridge">
      <p className="bfx-kicker">Bifrost trust exchange</p>
      <p className="bfx-bridge-quote">
        {idle ? "Communication is not trust." : refused ? "Refused, and recorded." : permitted ? "Verified. Not assumed." : line}
      </p>
      <div className="bfx-track">
        <div className="bfx-track-line" />
        <span
          className={refused ? "bfx-packet is-bad" : "bfx-packet"}
          style={{
            left: `calc(${8 + travel * 14}% )`,
            boxShadow: refused ? "0 0 16px #ff8a7a" : idle ? "none" : "0 0 16px #6fd3ff",
            opacity: idle ? 0.35 : 1,
          }}
          aria-hidden="true"
        />
      </div>
      <p className={refused ? "bfx-status is-bad" : permitted ? "bfx-status is-ok" : "bfx-status"}>
        {refused ? <BanIcon /> : permitted ? <CheckIcon /> : null}
        {status}
      </p>
    </div>
  );
}

function Artifact({
  scenario,
  settled,
  independent,
  onIndependent,
}: {
  scenario: Scenario;
  settled: boolean;
  independent: boolean;
  onIndependent: (next: boolean) => void;
}) {
  const refused = scenario.verdict === "deny";
  return (
    <article className="bfx-panel">
      <p className="bfx-kicker">{settled ? (refused ? "Refusal record" : "Signed receipt") : "Artifact"}</p>
      {settled ? (
        <>
          <h2>{refused ? "A refusal is part of the trust model." : "Verified without taking Bifrost’s word."}</h2>
          <p className="bfx-id">{scenario.artifactId}</p>
          <p className="bfx-muted">{scenario.reason}</p>
          <div className="bfx-actions" role="group" aria-label="How the counterparty checks the record">
            <button
              type="button"
              aria-pressed={independent}
              onClick={() => onIndependent(true)}
              className={independent ? "bfx-btn bfx-chip is-on" : "bfx-btn"}
            >
              Examine independently
            </button>
            <button
              type="button"
              aria-pressed={!independent}
              onClick={() => onIndependent(false)}
              className={!independent ? "bfx-btn bfx-chip is-on" : "bfx-btn"}
            >
              Vendor assertion only
            </button>
          </div>
          {!independent ? <p className="bfx-warn">A vendor assertion is not verification.</p> : null}
          <ul className="bfx-checks">
            {CHECKS.map(([label, detail]) => (
              <li key={label} className={independent && !refused ? "bfx-check is-ok" : "bfx-check"}>
                <p>{label}</p>
                <small>
                  {independent
                    ? refused && label === "Evidence refs"
                      ? "No bytes were released to hash."
                      : detail
                    : "Not examined"}
                </small>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <h2>No record yet.</h2>
          <p className="bfx-muted">
            A signed message is not, by itself, proof a business action was permitted. The receipt
            appears only after identity, authority, and policy have been evaluated.
          </p>
        </>
      )}
    </article>
  );
}

function BridgeMark() {
  return (
    <svg viewBox="0 0 32 32" className="bfx-mark" aria-hidden="true">
      <path d="M4 23c6-13 18-13 24 0" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 23c4.4-8.2 11.6-8.2 16 0" fill="none" stroke="currentColor" strokeWidth="1.6" opacity="0.65" />
      <path d="M12 23c2.4-4 5.6-4 8 0" fill="none" stroke="currentColor" strokeWidth="1.6" opacity="0.4" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M7 5v14l12-7z" fill="currentColor" stroke="none" />
    </svg>
  );
}

function ResetIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
    </svg>
  );
}

function BanIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M6 6l12 12" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.5l2.5 2.5L16 9.5" />
    </svg>
  );
}
